/**
 * VersionResolutionPolicy (AJIP-16.02.006 · WS5)
 * ---------------------------------------------------------------------
 * Deterministic Journey Version resolution. Delegates to the protected
 * VersioningPolicy primitives from AJIP-16.02.005.
 *
 * Rules
 *   * Honour an explicitly requested version when it matches the
 *     supported set.
 *   * Select the current approved version when no version is supplied.
 *   * Reject unsupported versions.
 *   * Identify deprecated versions and surface replacementJourneyId.
 *   * Never silently redirect from one journey definition to another.
 */
import { assertNotDeprecated } from '../registrationDiscovery/VersioningPolicy';
import { RESOLUTION_DECISIONS } from './ResolutionDecisionContract';

const VERSION_OUTCOMES = Object.freeze({
  RESOLVED: 'RESOLVED',
  VERSION_UNSUPPORTED: 'VERSION_UNSUPPORTED',
  DEPRECATED: 'DEPRECATED',
});

const resolveVersion = ({ definition, requestedVersion = null, supportedVersions = null }) => {
  if (!definition || typeof definition !== 'object') {
    return Object.freeze({ outcome: VERSION_OUTCOMES.VERSION_UNSUPPORTED, chosen: null, reason: 'DEFINITION_MISSING' });
  }
  const registeredVersion = definition.journeyVersion;

  // Support set defaults to a single-element list containing the
  // registered version when the caller supplies none.
  const supported = Array.isArray(supportedVersions) && supportedVersions.length > 0
    ? supportedVersions
    : [registeredVersion];

  if (requestedVersion !== null && requestedVersion !== undefined) {
    if (!Number.isInteger(requestedVersion) || requestedVersion < 1) {
      return Object.freeze({
        outcome: VERSION_OUTCOMES.VERSION_UNSUPPORTED,
        chosen: null,
        reason: 'INVALID_REQUESTED_VERSION',
      });
    }
    if (!supported.includes(requestedVersion)) {
      return Object.freeze({
        outcome: VERSION_OUTCOMES.VERSION_UNSUPPORTED,
        chosen: null,
        reason: `VERSION_${requestedVersion}_UNSUPPORTED`,
      });
    }
    // No silent redirect — the chosen version must equal the requested
    // one within the same journeyId.
    if (requestedVersion !== registeredVersion) {
      // The registered version differs from the requested one. That is
      // acceptable only if the supported set contains the requested
      // version AND the definition itself models the requested version.
      // In the current implementation each journeyId registers exactly
      // one version, so unequal values are rejected.
      return Object.freeze({
        outcome: VERSION_OUTCOMES.VERSION_UNSUPPORTED,
        chosen: null,
        reason: 'REQUESTED_VERSION_NOT_REGISTERED',
      });
    }
  }

  // Deprecation check.
  try {
    assertNotDeprecated(definition);
  } catch (_e) {
    return Object.freeze({
      outcome: VERSION_OUTCOMES.DEPRECATED,
      chosen: null,
      reason: definition.deprecationStatus.toUpperCase(),
      replacementJourneyId: definition.replacementJourneyId || null,
    });
  }

  return Object.freeze({
    outcome: VERSION_OUTCOMES.RESOLVED,
    chosen: registeredVersion,
    reason: 'OK',
  });
};

const versionOutcomeToDecision = (outcome) => {
  switch (outcome) {
    case VERSION_OUTCOMES.RESOLVED: return RESOLUTION_DECISIONS.RESOLVED;
    case VERSION_OUTCOMES.VERSION_UNSUPPORTED: return RESOLUTION_DECISIONS.VERSION_UNSUPPORTED;
    case VERSION_OUTCOMES.DEPRECATED: return RESOLUTION_DECISIONS.DEPRECATED;
    default: return RESOLUTION_DECISIONS.INVALID_REQUEST;
  }
};

export { VERSION_OUTCOMES, resolveVersion, versionOutcomeToDecision };
